/**
 * "Plot View" table for the Plot Matrix (JSON) tab — renders purely from an
 * already-built Plot Matrix JSON (`columns`/`rows`/`columnTotals`/
 * `grandTotal` — see `plotMatrixJsonBuilder.ts`). No tender state, no API
 * calls, no calculation logic — the JSON is the only input. Column grouping
 * (main header / sub header, flat-vs-nested) matches `PlotMatrix.tsx`'s own
 * default view exactly — see `plotMatrixJsonPlotView.ts`.
 */
import React, { useMemo } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatCurrency } from "../../../../utils/calculations";
import {
  calculateRowTotal,
  type PlotMatrixCategoryColumn,
  type PlotMatrixRow,
} from "../../../../utils/plotMatrixCategoryGenerator";
import { groupPlotMatrixColumnsForPlotView } from "../../../../utils/plotMatrixJsonPlotView";
import {
  insertGarageSeparatorRow,
  type PlotMatrixSeparatorRow,
} from "../../../../utils/plotMatrixRowSeparator";

export interface PlotMatrixJsonPlotTableProps {
  columns: PlotMatrixCategoryColumn[];
  rows: PlotMatrixRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
}

type DisplayRow = PlotMatrixRow | PlotMatrixSeparatorRow;

const isSeparatorRow = (record: DisplayRow): record is PlotMatrixSeparatorRow =>
  (record as PlotMatrixSeparatorRow)._isSeparator === true;

const headerLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  color: "#1f2937",
  fontSize: 14,
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "center",
};

const PlotMatrixJsonPlotTable: React.FC<PlotMatrixJsonPlotTableProps> = ({
  columns,
  rows,
  columnTotals,
  grandTotal,
}) => {
  const rowsWithSeparator = useMemo<DisplayRow[]>(
    () => insertGarageSeparatorRow(rows),
    [rows],
  );
  const totalColSpan = 2 + columns.length + 1;

  const tableColumns = useMemo<ColumnsType<DisplayRow>>(() => {
    const groups = groupPlotMatrixColumnsForPlotView(columns);

    const groupedColumns: ColumnsType<DisplayRow> = groups.map((group) => {
      const leafColumns: ColumnsType<DisplayRow> = group.columns.map(({ column, subHeader }) => ({
        title: (
          <Tooltip title={subHeader}>
            <span style={{ ...headerLabelStyle, fontWeight: 600, color: "#1890ff", fontSize: 13 }}>
              {subHeader}
            </span>
          </Tooltip>
        ),
        key: column.key,
        dataIndex: column.dataIndex,
        align: "right" as const,
        width: 150,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
        render: (cell: { total?: number } | undefined, record: DisplayRow) => {
          if (isSeparatorRow(record)) return null;
          return formatCurrency(cell?.total ?? 0);
        },
      }));

      if (group.flat) {
        return {
          ...leafColumns[0],
          title: (
            <Tooltip title={group.mainHeader}>
              <span style={headerLabelStyle}>{group.mainHeader}</span>
            </Tooltip>
          ),
        };
      }

      return {
        title: (
          <Tooltip title={group.mainHeader}>
            <span style={headerLabelStyle}>{group.mainHeader}</span>
          </Tooltip>
        ),
        key: `group-${group.mainHeader}`,
        children: leafColumns,
      };
    });

    return [
      {
        title: "Plot No",
        key: "plotNo",
        dataIndex: "plotNo",
        fixed: "left",
        width: 100,
        onCell: (record: DisplayRow) =>
          isSeparatorRow(record) ? { colSpan: totalColSpan } : {},
        render: (value: string, record: DisplayRow) => {
          if (isSeparatorRow(record)) {
            return (
              <strong style={{ fontSize: 13, color: "#374151", letterSpacing: "0.05em" }}>
                GARAGES
              </strong>
            );
          }
          return value;
        },
      },
      {
        title: "House Type",
        key: "housetype",
        dataIndex: "housetype",
        fixed: "left",
        width: 150,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
      },
      ...groupedColumns,
      {
        title: "Row Total",
        key: "rowTotal",
        fixed: "right",
        width: 150,
        align: "right" as const,
        onCell: (record: DisplayRow) => (isSeparatorRow(record) ? { colSpan: 0 } : {}),
        render: (_: unknown, row: DisplayRow) => {
          if (isSeparatorRow(row)) return null;
          return formatCurrency(calculateRowTotal(row));
        },
      },
    ];
  }, [columns, totalColSpan]);

  return (
    <Table<DisplayRow>
      className="plot-matrix-json-table"
      columns={tableColumns}
      dataSource={rowsWithSeparator}
      rowKey="key"
      pagination={false}
      scroll={{ x: "max-content" }}
      bordered
      size="small"
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <strong>GRAND TOTAL</strong>
            </Table.Summary.Cell>
            {columns.map((col, idx) => (
              <Table.Summary.Cell key={col.dataIndex} index={idx + 2} align="right">
                <strong>{formatCurrency(columnTotals[col.dataIndex] ?? 0)}</strong>
              </Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={columns.length + 2} align="right">
              <strong>{formatCurrency(grandTotal)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
};

export default PlotMatrixJsonPlotTable;
